
import Vue, { PropType } from 'vue'
import api from '@root/src/api/api'
import SpinnerLoader from '@root/src/components/SpinnerLoader.vue'
import Opz2Metrics from './Opz2Metrics.vue'
import Opz2Empty from './Opz2Empty.vue'
import {
    CrossChip,
    EMPTY_DICTIONARIES,
    Opz2Dictionaries,
    crmAdminLabel,
    clientName,
    formatDate,
    formatNumber,
    formatPercent,
    matchesChip,
    statusLabel,
    statusTag,
} from './opz2'

type ChipId = CrossChip

export default Vue.extend({
    name: 'Opz2CrossTab',
    components: { SpinnerLoader, Opz2Metrics, Opz2Empty },
    props: {
        projectId: { type: String, required: true },
        filters: { type: Object as PropType<any>, required: true },
        active: { type: Boolean, default: false },
    },
    data: () => ({
        loading: false,
        error: false,
        requestId: 0,
        unclassifiedEvents: 0,
        reportData: null as any,
        dictionaries: EMPTY_DICTIONARIES as Opz2Dictionaries,
        activeChip: 'all' as ChipId,
        staffMarked: true,
        footerProps: { 'items-per-page-options': [10, 25, 50] },
        filterHelp: [
            { title: 'День в день', text: 'Запись создана на этот же день.' },
            { title: 'На будущее', text: 'Визит по ОПЗ позже даты создания записи.' },
            {
                title: 'Впервые',
                text:
                    'Клиента записали на новую для него услугу или к мастеру, у которого он ещё не был.',
            },
        ],
    }),
    computed: {
        table(): any[] {
            return this.reportData?.crossSailTable ?? []
        },
        hasData(): boolean {
            return this.table.length > 0
        },
        emptyTitle(): string {
            return this.staffMarked
                ? 'За выбранный период кросс-продаж нет'
                : 'Кросс-продажи пока не рассчитываются'
        },
        emptyHint(): string {
            // Отличаем «данных нет» от «данные нельзя рассчитать».
            return this.staffMarked
                ? 'Попробуйте выбрать другой период — фильтр работает по дате создания записи.'
                : 'Заполните связи мастеров и типов услуг на вкладке «Настройка KPI» — без них система не отличает ОПЗ 2.0 от кросс-продаж.'
        },
        total(): any {
            return this.reportData?.crossSailTotal ?? {}
        },
        metrics(): any[] {
            return [
                {
                    label: 'Кросс-продажи',
                    value: formatNumber(this.total.crossSailsCnt),
                    hint: 'в выбранном периоде',
                },
                {
                    label: 'Завершённые',
                    value: formatNumber(this.total.crossSailsSuccessCnt),
                    hint: `${formatPercent(this.total.crossSailsSuccessPercent)} состоялось визитов`,
                    tone: 'teal',
                },
                {
                    label: 'Впервые',
                    value: formatNumber(this.total.crossSailsFirstlyCnt),
                    hint: 'новая услуга или мастер',
                    tone: 'wasabi',
                },
                {
                    label: 'Отменено',
                    value: formatNumber(this.total.crossSailsCancelCnt),
                    hint: `${formatPercent(this.total.crossSailsCancelPercent)} отмен за период`,
                    tone: 'red',
                },
            ]
        },
        chips(): { id: ChipId; label: string }[] {
            return [
                { id: 'all', label: 'Все' },
                { id: 'same_day', label: 'День в день' },
                { id: 'future_day', label: 'На будущее' },
                { id: 'firstly', label: 'Впервые' },
            ]
        },
        headers(): any[] {
            return [
                { text: 'Клиент', value: 'client', align: 'left', width: '17%' },
                { text: 'Маршрут', value: 'route', align: 'left', width: '21%' },
                { text: 'Дата записи', value: 'visitDate', align: 'center', width: '12%' },
                { text: 'Визит по ОПЗ', value: 'opzVisitDate', align: 'center', width: '12%' },
                { text: 'Статус', value: 'status', align: 'center', width: '14%' },
                { text: 'Впервые', value: 'firstly', align: 'center', width: '9%' },
                { text: 'Аккаунт', value: 'account', align: 'left' },
            ]
        },
        rows(): any[] {
            return this.table
                .filter((row: any) => matchesChip(row, this.activeChip))
                .map((row: any) => ({
                    client: clientName(this.dictionaries, row.clientId),
                    routeFrom: row.staffSpecialization || 'Не задано',
                    routeTo: row.opzStaffSpecialization || 'Не задано',
                    visitDate: formatDate(row.visitDate),
                    opzVisitDate: formatDate(row.opzVisitDate),
                    status: statusLabel(row.opzStatus),
                    statusTag: statusTag(row.opzStatus),
                    firstly: row.opzIsFirstly ? 'Да' : 'Нет',
                    isFirstly: Boolean(row.opzIsFirstly),
                    account: crmAdminLabel(this.dictionaries, row.opzCreatingUserId),
                }))
        },
    },
    watch: {
        active: {
            immediate: true,
            handler() {
                this.loadIfNeeded()
            },
        },
        filters() {
            this.loadIfNeeded()
        },
    },
    beforeDestroy() {
        this.requestId++
    },
    methods: {
        loadIfNeeded() {
            if (!this.active) return
            this.load()
        },
        async load() {
            const requestId = ++this.requestId
            this.loading = true
            this.error = false
            this.$emit('loading', true)
            try {
                const { data, error } = await api.gcb2.getOpz2Data({
                    projectId: this.projectId,
                    filters: this.filters,
                    tabs: ['cross'],
                })
                if (requestId !== this.requestId) return
                if (error || !data) {
                    this.error = true
                    return
                }
                this.reportData = data.reportData
                this.unclassifiedEvents = Number(data.meta?.unclassifiedEvents ?? 0)
                this.dictionaries = data.dictionaries ?? EMPTY_DICTIONARIES
                this.staffMarked = data.meta?.staffMarked !== false
                this.activeChip = 'all'
            } catch (err) {
                if (requestId === this.requestId) this.error = true
            } finally {
                if (requestId === this.requestId) {
                    this.loading = false
                    this.$emit('loading', false)
                }
            }
        },
    },
})
